import React, { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { BILLING_ADDRESS } from "../../component/common/Routes/AppRoutes";
import "../../styles/AddressForm.css";
import ControlledTextInputField from "../../component/ui/FormFields/ControlledTextInputField";
import CustomButton from "../../component/ui/FormFields/Button";
import StatusRadioGroup from "../../component/ui/FormFields/StatusRadioGroup";
import {
  resetBillingAddressState,
  updateBillingAddressRequest,
  viewBillingAddressRequest,
} from "../../redux/BillingAddress/BillingAddressAction";
import { CUSTOMER_BILLING_ADDRESS } from "../../component/common/Routes/CustomerRoutes";
import {
  selectCityRequest,
  selectCountryRequest,
  selectStateRequest,
} from "../../redux/Common/selectorAction";
import Loading from "../../component/ui/Loading";
import { toast, ToastContainer } from "react-toastify";

export default function EditBillingAddressForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    loading: viewListLoading,
    updateLoading,
    success,
    error,
    data,
  } = useSelector((state) => state.billingAddress);
  const addressId = id || location.state?.form1Value?.id;
  const addressDetails = data?.data;
  useEffect(() => {
    dispatch(resetBillingAddressState());
  }, []);
  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error}`, {
        position: "top-center",
        autoClose: 3000,
      });
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      toast.success("Billing Address Updated Successfully", {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          dispatch(resetBillingAddressState());
          navigate(CUSTOMER_BILLING_ADDRESS);
        },
      });
    }
  }, [success]);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      company_name: "",
      address1: "",
      address2: "",
      pincode: "",
      city: "",
      state: "",
      country: "",
      country_code: "",
      gst_number: "",
      emergency_country_code: "",
      phone: "",
      emergency_contact: "",
      status: "",
    },
  });
  // address selector

  useEffect(() => {
    dispatch(selectCountryRequest());
  }, []);

  const formValue = watch();
  const countryId = formValue.country?.value;
  const stateId = formValue.state?.value;

  const countries = useSelector((state) => state.selector?.country?.data);
  // country code fetcher
  const countryForCode = countries?.map((value) => {
    return {
      label: ` +${value.phonecode} - ${value.short_name}`,
      value: value.phonecode,
    };
  });
  const states = useSelector((state) => state.selector?.state?.data);
  const cities = useSelector((state) => state.selector?.city?.data);

  const countryName = countries?.map((country) => {
    return { value: country?.id, label: country?.country_name };
  });
  const stateName = states?.map((state) => {
    return { value: state?.id, label: state?.state_name };
  });
  const cityName = cities?.map((city) => {
    return { value: city?.id, label: city?.city_name };
  });

  const stateCount = useRef(0);
  const cityCount = useRef(0);
  useMemo(() => {
    if (countryId > 0 && stateCount.current > 0) {
      setValue("state", "");
      setValue("city", "");
    }
    if (countryId > 0) {
      dispatch(selectStateRequest(countryId));
      stateCount.current += 1;
    }
  }, [countryId]);
  // change state
  useMemo(() => {
    if (stateId > 0 && cityCount.current > 0) {
      setValue("city", "");
    }
    if (stateId > 0) {
      dispatch(selectCityRequest(stateId));
      cityCount.current += 1;
    }
  }, [stateId]);
  useEffect(() => {
    if (addressId) {
      dispatch(viewBillingAddressRequest(addressId));
    }
  }, [dispatch, addressId]);

  useEffect(() => {
    if (addressDetails && Object.keys(addressDetails).length > 0) {
      // Reset the form values when userData changes
      reset({
        first_name: addressDetails.first_name || "",
        last_name: addressDetails.last_name || "",
        email: addressDetails.email || "",
        company_name: addressDetails.company_name || "",
        address1: addressDetails.address1 || "",
        address2: addressDetails.address2 || "",
        pincode: addressDetails.pincode || "",
        gst_number: addressDetails.gst_number || "",
        city: addressDetails.city
          ? {
              label: addressDetails.city_name?.replace("_", " ").toUpperCase(),
              value: addressDetails.city,
            }
          : null,
        state: addressDetails.state
          ? {
              label: addressDetails.state_name?.replace("_", " ").toUpperCase(),
              value: addressDetails.state,
            }
          : null,
        country: addressDetails.country
          ? {
              label: addressDetails.country_name
                ?.replace("_", " ")
                .toUpperCase(),
              value: addressDetails.country,
            }
          : null,
        country_code: addressDetails?.country_code
          ? {
              label: addressDetails?.country_code,
              value: addressDetails?.country_code,
            }
          : null,
        emergency_country_code: addressDetails?.emergency_country_code
          ? {
              label: addressDetails?.emergency_country_code,
              value: addressDetails?.emergency_country_code,
            }
          : null,
        phone: addressDetails.phone_number || "",
        emergency_contact: addressDetails.emergency_contact,
        status: addressDetails.status === 1 ? "Active" : "Inactive",
      });
    }
  }, [addressDetails, reset]);

  const fields = [
    {
      name: "first_name",
      label: "First Name *",
      rules: { required: "First Name is required" },
      placeholder: "Enter first name",
      sm: 6,
      xs: 12,
    },
    {
      name: "last_name",
      label: "Last Name *",
      rules: { required: "Last Name is required" },
      placeholder: "Enter last name",
      sm: 6,
      xs: 12,
    },
    {
      name: "company_name",
      label: "Company Name",
      placeholder: "Enter company name",
      sm: 6,
      xs: 12,
    },
    {
      name: "address1",
      label: "Address Line 1 *",
      rules: { required: "Address is required" },
      placeholder: "Enter address",
      sm: 6,
      xs: 12,
    },
    {
      name: "address2",
      label: "Address Line 2",
      // rules: { required: 'Address Line 2 is required' },
      placeholder: "Enter additional address",
      sm: 6,
      xs: 12,
    },
    {
      name: "country",
      label: "Country *",
      rules: { required: "Country is required" },
      placeholder: "Enter country",
      options: countryName,
      sm: 6,
      xs: 12,
    },
    {
      name: "state",
      label: "State *",
      rules: { required: "State is required" },
      placeholder: "Enter state",
      options: stateName,
      sm: 6,
      xs: 12,
    },
    {
      name: "city",
      label: "City *",
      rules: { required: "City is required" },
      placeholder: "Enter city",
      options: cityName,
      sm: 6,
      xs: 12,
    },

    {
      name: "pincode",
      label: "Pincode *",
      rules: {
        required: "Pincode is required",
        pattern: {
          value: /^[0-9]+$/,
          message: "Enter a valid numeric pincode",
        },
      },
      placeholder: "Enter pincode",
      sm: 6,
      xs: 12,
    },
    {
      name: "country_code",
      options: countryForCode,
      label: "code",
      rules: {
        required: "Code is Required",
      },
      sm: 2,
      xs: 4,
    },
    {
      name: "phone",
      label: "Phone Number *",
      placeholder: "Enter phone number",
      rules: {
        required: "Mobile Number is required",
        pattern: {
          value: /^\d+$/,
          message: "Enter only numeric digits",
        },
      },
      sm: 4,
      xs: 8,
    },
    {
      name: "emergency_country_code",
      options: countryForCode,
      label: "country Code *",
      rules: {
        required: "Code is Required",
      },
      sm: 2,
      xs: 4,
    },
    {
      name: "emergency_contact",
      label: "Emergency Contact *",
      placeholder: "Enter emergency contact",
      rules: {
         required: "Emergency Contact is Required",
        pattern: {
          value: /^\d+$/,
          message: "Enter only numeric digits",
        },
      },
      sm: 4,
      xs: 8,
    },
    {
      name: "gst_number",
      label: "GST Number",
      type: "text",
      sm: 6,
      xs: 12,
    },
  ];

  const onSubmit = (formData) => {
    const formDataWithStatus = {
      ...formData,
      city: formData.city?.value,
      state: formData.state?.value,
      country: formData.country?.value,
      gst_number: formData.gst_number,
      status: formData.status === "Active" ? 1 : 2,
      country_code: formData?.country_code?.value,
      emergency_country_code: formData?.emergency_country_code?.value || "-",
    };

    dispatch(
      updateBillingAddressRequest({ id: addressId, data: formDataWithStatus })
    );
  };
  if (viewListLoading) {
    return <Loading text="Loading Address details..." />;
  }

  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Card className="form-card">
          <CardContent>
            <Typography variant="h5">Edit Address</Typography>
            <Divider className="divider-line" />

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {fields.map((field, index) => (
                  <Grid item xs={field.xs} sm={field.sm} key={index}>
                    <ControlledTextInputField
                      name={field.name}
                      control={control}
                      label={field.label}
                      options={field.options}
                      placeholder={field.placeholder}
                      rules={field.rules}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]?.message}
                    />
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <StatusRadioGroup
                    name="status"
                    control={control}
                    defaultValue={data?.status === 1 ? "Active" : "Inactive"}
                    rules={{ required: "Status is required" }}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box className="button-container">
                  <CustomButton
                    variant="contained"
                    className="cancel-button"
                    type="button"
                    text={"Cancel"}
                    onClick={() => navigate(CUSTOMER_BILLING_ADDRESS)}
                  />
                  <CustomButton
                    variant="contained"
                    className="success-button"
                    type="submit"
                    loading={updateLoading}
                    text={"Submit"}
                  />
                </Box>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>{" "}
    </>
  );
}
